import { useState, useEffect, useCallback } from 'react';

export const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    userAgent: '',
    screenWidth: 0,
    screenHeight: 0,
    touchSupport: false,
    deviceType: 'desktop'
  });

  const detectMobile = useCallback(() => {
    // User Agent Detection
    const userAgent = navigator.userAgent || '';
    const mobileUserAgentRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    const isUserAgentMobile = mobileUserAgentRegex.test(userAgent);

    // Screen Size Detection
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const isSmallScreen = screenWidth <= 768 || screenHeight <= 1024;

    // Touch Support Detection
    const hasTouchSupport = 'ontouchstart' in window || 
                           navigator.maxTouchPoints > 0 || 
                           navigator.msMaxTouchPoints > 0;

    // Aspect Ratio Detection (mobile devices typically have tall aspect ratios)
    const aspectRatio = screenHeight / screenWidth;
    const isTallAspectRatio = aspectRatio > 1.3;

    // Enhanced Detection Logic
    let deviceType = 'desktop';
    let isMobileDevice = false;

    // Primary detection: User agent
    if (isUserAgentMobile) {
      isMobileDevice = true;
      if (/iPad/i.test(userAgent)) {
        deviceType = 'tablet';
      } else {
        deviceType = 'mobile';
      }
    }
    // Secondary detection: Screen size + touch + aspect ratio
    else if (isSmallScreen && hasTouchSupport && isTallAspectRatio) {
      isMobileDevice = true;
      deviceType = screenWidth >= 600 ? 'tablet' : 'mobile';
    }
    // Tertiary detection: Very small screens (likely mobile even without touch)
    else if (screenWidth <= 480) {
      isMobileDevice = true;
      deviceType = 'mobile';
    }

    setIsMobile(isMobileDevice);
    setDeviceInfo({
      userAgent,
      screenWidth,
      screenHeight,
      touchSupport: hasTouchSupport,
      deviceType
    });

    return isMobileDevice;
  }, []);

  useEffect(() => {
    // Initial detection
    detectMobile();

    // Re-detect on window resize (handles device rotation, window resizing)
    const handleResize = () => {
      setTimeout(() => detectMobile(), 100); // Small delay to ensure dimensions are updated
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [detectMobile]);

  return {
    isMobile,
    deviceInfo,
    redetect: detectMobile
  };
};

export default useMobileDetection; 